import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LuBuilding2,
  LuCalendar,
  LuClipboardList,
  LuDatabase,
  LuEye,
  LuFileText,
  LuPencil,
  LuPhone,
  LuTrash2,
  LuUser,
} from "react-icons/lu";

import OrderStatusBadge from "../materialLines/OrderStatusBadge";
import Button from "../ui/Button";
import SortableTableHead from "../ui/SortableTableHead";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useConfirmPopup from "../../context/useConfirmPopup";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { makeReport } from "../../services/download.service";
import { formatDateTime, formatQty } from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import {
  MaterialOrderListProps,
  MaterialOrderSortBy,
  MaterialOrderSortOrder,
} from "../../types/materialOrder";

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const actionGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const partyLinkClass = "font-semibold text-primary hover:underline break-words";

const contactIconClass = "h-3.5 w-3.5 flex-shrink-0 text-gray-400";

const detailPath = (id: number) =>
  route.materialOrderDetail.replace(":id", id as unknown as string);

const editPath = (id: number) =>
  route.materialOrderEdit.replace(":id", id as unknown as string);

const partyPath = (id: number) =>
  route.partyDetail.replace(":id", id as unknown as string);

// The contact name goes under the firm name; a party without a firm name shows its contact name in its place.
const contactName = (order: MaterialOrderListProps) =>
  order.party_firm_name ? order.party_name : null;

// The backend's reason for refusing to delete an order with purchases.
const lockedDeleteTitle =
  "આ ઓર્ડર ની મટીરીયલ ખરીદી માં એન્ટ્રી હોવાથી ડિલીટ કરી શકાશે નહીં.";

// Headers that also stand for other sorts: the date cell shows the order and creation dates, and the material cell the
// number of lines and the total qty.
const alsoSortsFor: Partial<Record<MaterialOrderSortBy, MaterialOrderSortBy[]>> = {
  date: ["created_at", "updated_at"],
  items_count: ["total_qty"],
};

type Props = {
  orders: MaterialOrderListProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  // The order the orders were loaded in.
  sortBy: MaterialOrderSortBy;
  sortOrder: MaterialOrderSortOrder;
  onSort: (sortBy: MaterialOrderSortBy, sortOrder: MaterialOrderSortOrder) => void;
  // The names of the list's sorts, for the headers' hover text.
  sortLabels: Record<MaterialOrderSortBy, string>;
  handleDeleteOrder: (id: number) => void;
};

const MaterialOrderList = ({
  orders,
  pageNumber,
  pageSize,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  sortLabels,
  handleDeleteOrder,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersDelete
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersReport
  );

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const actionCount =
    1 + (canReport ? 1 : 0) + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteOrder(id);
      setIsActionLoading(0);
    }
  };

  const handleExportReport = async (id: number) => {
    setIsActionLoading(id);
    const file = await makeReport(`/v1/admin/material-orders/${id}/pdf`);
    setIsActionLoading(0);

    if (file) dispatch(updatePdfFile(file));
  };

  const sortHead = (column: MaterialOrderSortBy, label: string) => (
    <SortableTableHead
      column={column}
      alsoFor={alsoSortsFor[column]}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      sortLabels={sortLabels}
      disabled={isLoading}
    >
      {label}
    </SortableTableHead>
  );

  const orderNumber = (order: MaterialOrderListProps) => (
    <span className="inline-flex items-center gap-2 font-semibold text-primary">
      <LuClipboardList className="h-4 w-4 flex-shrink-0 text-gray-400" />
      <span>#{order.id}</span>
    </span>
  );

  const partyDetails = (order: MaterialOrderListProps, isMobile: boolean) => {
    const contact = contactName(order);

    return (
      <div className={cx("min-w-0", isMobile && "text-right")}>
        <Link
          to={partyPath(order.party_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={partyLinkClass}
        >
          {order.party_firm_name || order.party_name || "-"}
        </Link>

        {(contact || order.party_phone) && (
          <div
            className={cx(
              "mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500",
              isMobile && "justify-end"
            )}
          >
            {contact && (
              <span className="flex items-center gap-1 break-words">
                <LuUser className={contactIconClass} />
                {contact}
              </span>
            )}

            {order.party_phone && (
              <span className="flex items-center gap-1">
                <LuPhone className={contactIconClass} />
                {order.party_phone}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const materialCount = (order: MaterialOrderListProps) =>
    `${formatQty(order.items_count)} મટીરીયલ`;

  const actionButtons = (order: MaterialOrderListProps, isMobile: boolean) => {
    // An order with purchases can't be deleted: its delete button stays, disabled, and says why on hover.
    const isDeleteLocked = order.purchases_count > 0;
    const buttonProps = isMobile
      ? { variant: "outline" as const, className: "w-full" }
      : { variant: "ghost" as const, size: "iconSm" as const };

    return (
      <>
        <Link to={detailPath(order.id)}>
          <Button {...buttonProps} title="વિગતો જુઓ">
            <LuEye className="h-4 w-4" />
          </Button>
        </Link>

        {canReport && (
          <Button
            {...buttonProps}
            title="રિપોર્ટ એક્સપોર્ટ"
            className={cx(
              buttonProps.className,
              "text-green-600 hover:text-green-700"
            )}
            onClick={() => handleExportReport(order.id)}
          >
            <LuFileText className="h-4 w-4" />
          </Button>
        )}

        {canUpdate && (
          <Button
            {...buttonProps}
            title="સંપાદિત કરો"
            onClick={() => navigate(editPath(order.id))}
          >
            <LuPencil className="h-4 w-4" />
          </Button>
        )}

        {canDelete && (
          <span
            title={isDeleteLocked ? lockedDeleteTitle : undefined}
            className={isMobile ? "block" : "inline-flex"}
          >
            <Button
              {...buttonProps}
              title="કાઢી નાખો"
              disabled={isDeleteLocked}
              className={cx(
                buttonProps.className,
                "text-red-600 hover:text-red-700"
              )}
              onClick={() => handleDeleteRow(order.id)}
            >
              <LuTrash2 className="h-4 w-4" />
            </Button>
          </span>
        )}
      </>
    );
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* ક્રમ and ઓર્ડર both stand for the order ID. */}
                  {sortHead("id", "ક્રમ")}
                  {sortHead("id", "ઓર્ડર")}
                  {sortHead("party", "પાર્ટી")}
                  {sortHead("items_count", "મટીરીયલ")}
                  {sortHead("status", "સ્થિતિ")}
                  {sortHead("date", "તારીખ")}
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    એકશન્સ બટન
                  </TableHead>
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={7} />
              ) : orders.length === 0 ? (
                <TableEmptyRow
                  colSpan={7}
                  label="મટીરીયલ ઓર્ડર ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {orders.map((order, index) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-semibold text-primary">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>{orderNumber(order)}</TableCell>

                      <TableCell>
                        <div className="flex items-start gap-2">
                          <LuBuilding2 className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
                          {partyDetails(order, false)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="font-medium text-primary">
                          {materialCount(order)}
                        </div>
                        <div className="text-sm text-gray-500">
                          કુલ જથ્થો: {formatQty(order.total_qty)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <OrderStatusBadge status={order.status} />
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDateTime(order.date)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(order.created_at)}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isActionLoading === order.id ? (
                            <div className={actionSpinnerClass}></div>
                          ) : (
                            actionButtons(order, false)
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : orders.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="મટીરીયલ ઓર્ડર ની વિગતો મળી નથી" />
          </div>
        ) : (
          orders.map((order, index) => (
            <div key={order.id} className={mobileCardClass}>
              <div className="flex items-center justify-between gap-2 border-b p-4">
                {orderNumber(order)}
                <OrderStatusBadge status={order.status} />
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પાર્ટી</span>
                  {partyDetails(order, true)}
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>મટીરીયલ</span>
                  <span className="text-right">
                    <span className="font-medium text-primary">
                      {materialCount(order)}
                    </span>
                    <span className="block text-sm text-gray-500">
                      કુલ જથ્થો: {formatQty(order.total_qty)}
                    </span>
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDateTime(order.date)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(order.created_at)}
                    </span>
                  </span>
                </div>
              </div>

              <div
                className={cx(
                  "grid gap-2 border-t p-4",
                  actionGridClass[actionCount]
                )}
              >
                {isActionLoading === order.id ? (
                  <div className="col-span-full flex h-10 items-center justify-center">
                    <div className={actionSpinnerClass}></div>
                  </div>
                ) : (
                  actionButtons(order, true)
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default MaterialOrderList;
